import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Immutable from 'seamless-immutable';
import get from 'lodash/get';
import {
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Icon,
} from '@material-ui/core';
import withWidth from '@material-ui/core/withWidth';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PhoneIcon from '@material-ui/icons/Phone';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AlarmIcon from '@material-ui/icons/Alarm';
import add from 'date-fns/add';

import Button from '../../core/components/Button';
import DialogView from '../../core/components/DialogView';
import MapComponent from '../../locationsPage/subComponents/MapComponent';
import {
  getCurrentOrder,
  getDialogLoading,
  getCurrentUserAddresses,
  getProducts,
} from '../../../selectors';
import '../../../css/checkout/subComponents/OrderLocationDialog.scss';
import {
  DELIVERY,
  DINE_IN,
  CATERING,
  PICKUP,
  WEEKDAYARRAY,
} from '../../../services/constants/Constants';
import {
  getOutOfStockItems,
  getDayWorkingHours,
  getTime,
  isMobileMode,
  getFirstTableNumber,
  availableDeliveryOptions,
  locationIsOpen24HoursForDeliveryOption,
} from '../../../services/functions/Functions';
import * as FeatureFlags from '../../../../feature-flags.json';

const deliveryOptionText = {
  DELIVERY: 'CheckoutDrawer.orderLocationDialog.delivery',
  PICKUP: 'CheckoutDrawer.orderLocationDialog.pickup',
  DINE_IN: 'CheckoutDrawer.orderLocationDialog.dine_in',
  CATERING: 'CheckoutDrawer.orderLocationDialog.catering',
};

// TO-DO: Remove inline styles
const mapContainerStyle = {
  height: '35vh',
  position: 'relative',
};

const mapStyle = {
  width: 'auto',
  height: '100%',
  margin: 'auto',
  borderColor: '#000000',
  borderWidth: '2px',
  borderRadius: '1px',
  borderStyle: 'solid',
};

class OrderLocationDialog extends React.Component {
  constructor(props) {
    super(props);
    const firstStoreLocationId = get(props, 'storeLocations[0].id');
    this.state = {
      selectedLocationId: props.selectedLocationId || firstStoreLocationId,
      invalidLocationId: null,
    };
  }

  componentDidUpdate(prevProps) {
    const firstStoreLocationId = get(this.props, 'storeLocations[0].id');
    const prevPropsFirstStoreLocationId = get(prevProps, 'storeLocations[0].id');
    // If there is a delay in sorting locations and the selectedLocationId is not previously set
    if (prevPropsFirstStoreLocationId !== firstStoreLocationId && !this.props.selectedLocationId) {
      this.setState({ selectedLocationId: firstStoreLocationId });
    }
  }

  getSelectedLocation = (locations) => {
    const { selectedLocationId } = this.state;
    if (!locations) return null;
    return locations.find(location => location.id === selectedLocationId);
  }

  setOrderLocation = async (locations) => {
    const {
      deliveryOption,
      actions,
      user,
      currentOrder,
      handleChange,
      closeAfterChange,
    } = this.props;

    const selectedLocation = this.getSelectedLocation(locations);

    const updatedOrder = this.getUpdatedOrder(deliveryOption, selectedLocation, currentOrder);
    handleChange(selectedLocation.id);
    try {
      const response = await actions.updateOrder(closeAfterChange ? user : null, updatedOrder, currentOrder.id);

      if (response && !response.error) {
        this.handleClose();
      } else {
        const dialogObj = {
          dialog: 'locationSelect',
          openOrderLocationDialog: true,
        };
        if (closeAfterChange) this.props.onCloseDialog(dialogObj);
      }
    } catch (error) {
      console.log('API error for setOrderLocation', error);
    }
  }

  getUpdatedOrder = (deliveryOption, selectedLocation, currentOrder) => {
    if ([PICKUP, DINE_IN].includes(deliveryOption)) {
      return this.updateOrderToPickup(selectedLocation, currentOrder, deliveryOption);
    }
    // Handle delivery/catering locations
    return this.updateOrderToDelivery(selectedLocation, currentOrder, deliveryOption);
  }

  getOneDayHours = (dateObj, hours, deliveryOption, iterationCount) => {
    const { translation } = this.props;
    if (Array.isArray(hours) && hours.length === 0) return translation('HoursComponent.locationUnavailableHours');

    // At 8 iterations you've cycled through the entire week
    if (iterationCount === 8) return '';
    const todayWorkingHours = getDayWorkingHours(dateObj, hours);

    // If a day has no hours defined, but other days might
    if (!todayWorkingHours && iterationCount < 8) {
      const nextDay = add(dateObj, { days: 1 });
      return this.getOneDayHours(nextDay, hours, deliveryOption, iterationCount + 1);
    }
    const {
      open, close, pickupsClosed, deliveryOpen, deliveryClose, deliveriesClosed,
    } = todayWorkingHours[0];

    // Delivery and Catering right now cannot show the location hours from the addresses dialog here.
    // For them, the location info currently comes after you select the address, because the API
    // needs to check what location's delivery zone the address falls within.
    const isDeliveryOrCatering = [DELIVERY, CATERING].includes(deliveryOption);

    const isClosedToday = isDeliveryOrCatering
      ? deliveriesClosed
      : pickupsClosed;

    if (!isClosedToday) {
      const todayWeekday = WEEKDAYARRAY[dateObj.getDay()];
      const abbreviated = todayWeekday.slice(0, 3);

      if (locationIsOpen24HoursForDeliveryOption(todayWorkingHours[0], deliveryOption)) {
        const open24HoursText = `${abbreviated} ${translation('HoursComponent.open24Hours')}`;
        return open24HoursText;
      }
      const formattedOpen = isDeliveryOrCatering ? getTime(deliveryOpen) : getTime(open);
      const formattedClose = isDeliveryOrCatering ? getTime(deliveryClose) : getTime(close);
      if (!formattedOpen || !formattedClose) return translation('HoursComponent.locationUnavailableHours');
      return `${abbreviated} ${formattedOpen} - ${formattedClose}`;
    }

    const nextDay = add(dateObj, { days: 1 });
    return this.getOneDayHours(nextDay, hours, deliveryOption, iterationCount + 1);
  }

  // Replace currentOrder.deliveryOption with 'PICKUP' or 'DINE_IN'
  // Replace currentOrder.location with selectedLocation
  updateOrderToPickup = (selectedLocation, currentOrder, deliveryOption) => {
    const pickupOrder = Immutable.asMutable(currentOrder, { deep: true });
    pickupOrder.deliveryOption = deliveryOption;
    pickupOrder.location = Immutable.asMutable(selectedLocation, { deep: true });
    if (pickupOrder.location) {
      delete pickupOrder.location['delivery-option-messages'];
    }
    if (pickupOrder.deliveryOption === DINE_IN && selectedLocation.tableNumbers) {
      // Make sure our order has a table number set.
      // If there's no table number, set the first one.
      if (!pickupOrder.tableNumber) pickupOrder.tableNumber = getFirstTableNumber(selectedLocation);
      // If it already has one, check if the current table number is valid for the location.
      const selectedLocationTables = selectedLocation.tableNumbers.split(',');
      if (!selectedLocationTables.includes(pickupOrder.tableNumber)) pickupOrder.tableNumber = getFirstTableNumber(selectedLocation);
    }
    return pickupOrder;
  }

  // Replace currentOrder.deliveryOption with 'DELIVERY' or 'CATERING'
  // Add obj selectedLocation to currentOrder
  updateOrderToDelivery = (selectedLocation, currentOrder, deliveryOption) => {
    const deliveryOrder = Immutable.asMutable(currentOrder, { deep: true });
    deliveryOrder.deliveryOption = deliveryOption;
    deliveryOrder.address = selectedLocation;
    return deliveryOrder;
  }

  handleClose = () => {
    const { onCloseDialog } = this.props;
    onCloseDialog();
  }

  handleChange = (location) => {
    const { currentOrder, products } = this.props;
    // IN HERE
    // if location.id is having out of stock items, set state to render error message
    // else set the selectedLocationId
    const outOfStockItems = getOutOfStockItems(location.id, currentOrder, products);
    if (outOfStockItems.length > 0) {
      this.setState({ invalidLocationId: location.id });
    } else {
      this.setState({
        selectedLocationId: location.id,
      });
    }
  }

  handleClickAdd = () => {
    const dialogObj = {
      dialog: 'newAddress',
      openDeliveryAddressDialog: true,
    };
    if (this.props.onChangeDialog) {
      this.props.onChangeDialog();
    } else {
      this.props.onCloseDialog(dialogObj);
    }
  }

  generateKey = index => (`key${index}`);

  dialogContent = (deliveryOption, locations) => {
    const {
      currentOrder,
      loading,
      products,
      translation,
      width,
    } = this.props;
    const { invalidLocationId } = this.state;
    const currentLocation = this.getSelectedLocation(locations);
    const outOfStockItems = getOutOfStockItems(invalidLocationId, currentOrder, products);

    const hideMapComponentOnMobile = FeatureFlags.CheckoutDrawer.OrderLocationDialog.hideMapComponentOnMobile
      && isMobileMode(width);

    return (
      invalidLocationId
        ? (
          <div>
            <div className="orderLocationDialog-outOfStockMessageContainer">
              <Typography className="orderLocationDialog-outOfStockDialogMessage">
                {translation('CheckoutDrawer.orderLocationDialog.outOfStockMessage')}
              </Typography>
            </div>
            <div className="orderLocationDialog-outOfStockItems">
              {
                loading !== 0
                ? <CircularProgress />
                : (
                    outOfStockItems.map((item, index) => (
                      <Typography
                        className="orderLocationDialog-outOfStockItem"
                        key={this.generateKey(index)}
                      >
                        {item.productItem.name}
                      </Typography>
                    ))
                  )
              }
            </div>
            {
              loading === 0
              && (
                <div className="orderLocationDialog-outOfStockActionButtons">
                  {this.renderOutOfStockActionButtons(outOfStockItems)}
                </div>
              )
            }
          </div>
        )
        : (
          <div>
            <div>
              {
                !hideMapComponentOnMobile && locations && currentLocation
                && this.renderLocationMap(currentLocation)
              }
            </div>
            <div className="orderLocationDialog-locationsContainer">
              {
                locations
                && locations.map(location => (
                  this.renderLocationDetails(location, deliveryOption)
                ))
              }
              {
                this.renderAddButton(deliveryOption)
              }
            </div>
          </div>
        )
    );
  }

  showOneDayHours = (location, deliveryOption) => {
    const { hours } = location;
    // Delivery and Catering right now cannot show the location hours from the addresses dialog here.
    // For them, the location info currently comes after you select the address, because the API
    // needs to check what location's delivery zone the address falls within.
    if (!Array.isArray(hours)) return null;
    let oneDayHours = '';

    const today = new Date();
    oneDayHours = this.getOneDayHours(today, hours, deliveryOption, 0);

    return (
      <div className="orderLocationDialog-locationAddressSection">
        {
          oneDayHours
          && (
            <div>
              <AlarmIcon className="orderLocationDialog-clockIcon" />
            </div>
          )
        }
        <div>
          <Typography>
            {oneDayHours}
          </Typography>
        </div>
      </div>
    );
  }

  handleRemoveItems = async (outOfStockItems) => {
    const {
      actions, user, currentOrder,
    } = this.props;
    const mutableOrder = Immutable.asMutable(currentOrder, { deep: true });

    outOfStockItems.forEach(async (item) => {
      // items excluding removed item
      mutableOrder.items = mutableOrder.items.filter(cItem => cItem.id !== item.id);

      try {
        await actions.deleteOrderItem(user, item.id, currentOrder.id);
      } catch (error) {
        console.log('API delete order item error', error);
      }
    });
    await actions.updateOrder(user, mutableOrder, currentOrder.id);
    this.setState({ invalidLocationId: null });
  }

  navigateBack = () => this.props.navigateBack()

  renderPickupAddress = (location, deliveryOption) => {
    const {  width } = this.props;
    const { phone } = location;

    const addressSplit = location.address
      ? location.address.split(', ')
      : [];
    const street = addressSplit[0] || '';
    const city = addressSplit[1] || '';
    const province = addressSplit[2] || '';
    const postalCode = addressSplit[3] ? `| ${addressSplit[3]}` : '';

    const address = (
      <Fragment>
        {this.renderText(street)}
        {this.renderText(`${city}, ${province} ${postalCode}`)}
      </Fragment>
    );

    return (
      <div
        className="orderLocationDialog-locationAddressSubContainer"
        onClick={() => this.handleChange(location)}
      >
        {
          isMobileMode(width)
            ? (
              <Fragment>
                {this.renderAddressWithLocationIconForMobile(address)}
                <div className="orderLocationDialog-locationAddressSection">
                  {phone && <PhoneIcon className="orderLocationDialog-phoneIcon" />}
                  {this.renderText(phone, 'orderLocationDialog-paddedText')}
                </div>
                {this.showOneDayHours(location, deliveryOption)}
              </Fragment>
            )
            : (
              <Fragment>
                {address}
                {this.renderText(phone, 'orderLocationDialog-paddedText')}
                {this.showOneDayHours(location, deliveryOption)}
              </Fragment>
            )
        }
      </div>
    );
  }

  renderDeliveryAddress = (location) => {
    const { width } = this.props;

    const {
      nickname, unitNumber, streetAddress, city, postalCode,
    } = location;
    const unitNumberString = unitNumber ? `${unitNumber} - ` : '';
    const address = (
      <Fragment>
        {!isMobileMode(width) && this.renderText(nickname, 'orderLocationDialog-largerBoldText')}
        {this.renderText(`${unitNumberString}${streetAddress}`)}
        {this.renderText(`${city} | ${postalCode}`)}
      </Fragment>
    );

    return (
      <div
        className="orderLocationDialog-locationAddressSubContainer"
        onClick={() => this.handleChange(location)}
      >
        {
          isMobileMode(width)
            ? (
              <Fragment>
                {this.renderAddressWithLocationIconForMobile(address)}
              </Fragment>
            )
            : address
        }
      </div>
    );
  }

  renderLocationDetails = (location, deliveryOption) => {
    const { selectedLocationId } = this.state;

    // Store Location: name
    // User Address: nickname
    const name = location.name || location.nickname;

    const containerStyle = selectedLocationId === location.id
      ? 'orderLocationDialog-locationAddressSelectedContainer'
      : 'orderLocationDialog-locationAddressUnselectedContainer';

    return (
      <div
        key={`${name} ${location.id}`}
        className={containerStyle}
      >
        <FormControl className="orderLocationDialog-formControl">
          <RadioGroup
            aria-label={name}
            name={name}
            value={`${this.state.selectedLocationId}`}
            onChange={() => this.handleChange(location)}
            className="orderLocationDialog-radioGroup"
          >
            <FormControlLabel
              value={`${location.id}`}
              control={
                <Radio className="orderLocationDialog-formControlLabel" />
              }
              label={this.renderText(name, 'orderLocationDialog-largerBoldText')}
              className="orderLocationDialog-noMarginLeft"
            />
            {this.renderAddress(location, deliveryOption)}
          </RadioGroup>
        </FormControl>
      </div>
    );
  }

  renderAddress = (location, deliveryOption) => {
    if ([PICKUP, DINE_IN].includes(deliveryOption)) {
      return this.renderPickupAddress(location, deliveryOption);
    }

    return this.renderDeliveryAddress(location, deliveryOption);
  }

  renderAddButton = (deliveryOption) => {
    const {  translation } = this.props;
    return (
      (deliveryOption === 'DELIVERY' || deliveryOption === 'CATERING')
        && (
          <div className="orderLocationDialog-addNewAddressContainer">
            <div
              className="orderLocationDialog-addButton"
              onClick={() => this.handleClickAdd()}
            >
              <Icon>control_point</Icon>
              <Typography className="orderLocationDialog-addButtonText">
                {translation('CheckoutDrawer.orderLocationDialog.addNew')}
              </Typography>
            </div>
          </div>
        )
    );
  }

  renderLocationMap = (location) => {
    const { translation, history } = this.props;

    return (
      <MapComponent
        key={location.id}
        location={location}
        translation={translation}
        history={history}
        containerStyle={mapContainerStyle}
        mapStyle={mapStyle}
      />
    );
  }

  renderAddressWithLocationIconForMobile = (address) => (
    <div className="orderLocationDialog-locationAddressSection">
      <div className='orderLocationDialog-pinContainer'>
        <LocationOnIcon className="orderLocationDialog-locationPinIcon" />
      </div>
      <div>
        {address}
      </div>
    </div>
  )

  renderText = (text, style) => (
    <Typography className={style}>
      {text}
    </Typography>
  );

  renderActionButtons = (locations) => {
    const { translation } = this.props;
    const { invalidLocationId, selectedLocationId } = this.state;

    if (invalidLocationId) {
      return (
        <div />
      );
    }

    const multipleDeliveryOptions = availableDeliveryOptions().length > 1;

    return (
      <div className="orderLocationDialog-actionButtonContainer">
        {
          multipleDeliveryOptions
          && (
            <Button
              variant="text"
              overrideClass
              type="primary"
              className="orderLocationDialog-backButtonStyle"
              onClick={this.navigateBack}
              startIcon={<ArrowBackIcon className="orderLocationDialog-backArrowIcon" />}
              text={translation('BACK')}
            />
          )
        }
        <Button
          id="confirmLocation"
          className="confirmButton"
          overrideClass
          disabled={selectedLocationId === 0}
          type="primary"
          onClick={() => this.setOrderLocation(locations)}
          text={translation('CheckoutDrawer.orderLocationDialog.confirm')}
        />
      </div>
    );
  }

  renderOutOfStockActionButtons = (outOfStockItems) => {
    const { translation } = this.props;

    return (
      <div className="orderLocationDialog-actionButtonContainer">
        <Button
          type="secondary"
          onClick={() => {
            this.handleClose();
          }}
          text={translation('CANCEL')}
        />
        <Button
          type="primary"
          onClick={() => {
            this.handleRemoveItems(outOfStockItems);
          }}
          text={translation('REMOVE')}
        />
      </div>
    );
  }

  render() {
    const {
      deliveryOption,
      open,
      translation,
      userAddresses,
      storeLocations,
      dialogLoading,
      allowDialogClose,
    } = this.props;
    const { invalidLocationId } = this.state;
    const locations = ([PICKUP, DINE_IN].includes(deliveryOption))
      ? storeLocations
      : userAddresses;
    const dialogTitleText = invalidLocationId
      ? translation('CheckoutDrawer.orderLocationDialog.productsOutOfStock')
      : translation(deliveryOptionText[deliveryOption]);

    const dialogTitleStyle = allowDialogClose
      ? 'orderLocationDialog-dialogTitleStyle'
      : 'orderLocationDialog-dialogTitleWithPadding';

    return (
      <DialogView
        open={open}
        titleAlignClose={false}
        titleHasCloseBtn={allowDialogClose}
        handleClose={() => this.handleClose()}
        dialogCloseIconColor="orderLocationDialog-dialogCloseIconColor"
        disableBackdropClick={!allowDialogClose}
        disableEscapeKeyDown={!allowDialogClose}
        // TO-DO: Remove inline styles
        dialogPaperStyle={{ width: '875px' }}
        dialogBodyContainerStyle="orderLocationDialog-dialogBodyContainerStyle"
        dialogContentStyle="orderLocationDialog-dialogContentStyle"

        dialogTitleText={dialogTitleText}
        dialogTitleStyle={dialogTitleStyle}
        dialogTitleStyleVariant="title"

        hasDialogContent
        renderDialogContent={() => this.dialogContent(deliveryOption, locations)}

        hasDialogContent2={false}

        hasDialogActions
        actionBtnStyle={{
          padding: '16px',
        }
        }
        renderActionBtn={() => this.renderActionButtons(locations)}
        loading={!!dialogLoading}
      />
    );
  }
}

OrderLocationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  deliveryOption: PropTypes.string.isRequired,
  translation: PropTypes.func.isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  onCloseDialog: PropTypes.func.isRequired,
  currentOrder: PropTypes.objectOf(PropTypes.any).isRequired,
  dialogLoading: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  navigateBack: PropTypes.func.isRequired,
  selectedLocationId: PropTypes.number,
  userAddresses: PropTypes.arrayOf(PropTypes.object),
  storeLocations: PropTypes.arrayOf(PropTypes.any),
  closeAfterChange: PropTypes.bool,
  onChangeDialog: PropTypes.func,
  user: PropTypes.objectOf(PropTypes.any),
  products: PropTypes.arrayOf(PropTypes.object),
  allowDialogClose: PropTypes.bool,
};

OrderLocationDialog.defaultProps = {
  user: null,
  userAddresses: null,
  storeLocations: null,
  closeAfterChange: true,
  onChangeDialog: null,
  selectedLocationId: null,
  products: null,
  allowDialogClose: true,
};

const mapStateToProps = state => ({
  currentOrder: getCurrentOrder(state),
  dialogLoading: getDialogLoading(state),
  userAddresses: getCurrentUserAddresses(state),
  products: getProducts(state),
});

export default connect(mapStateToProps)(withWidth()(OrderLocationDialog));
